import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  AlertCircle, Activity, User, ClipboardList, Clock,
  Baby, Heart, Truck, AlertTriangle, Home,
  ChevronDown, ChevronUp, ChevronsDown, ChevronsUp, FileEdit
} from 'lucide-react';
import { FormPageLayout, DocStatusPill } from '../../common';
import type { ActionButtonConfig } from '../../common';
import { CarePlanStatus } from '../../../services/patientmr/careplans.service';
import { useCarePlanDraft } from './hooks/useCarePlanDraft';
import GoalsEditor from './goals/GoalsEditor';
import OrderingPhysicianSection from './sections/OrderingPhysicianSection';
import CertPeriodSection, { applyCertPeriod, hasFixedCertPeriod } from './sections/CertPeriodSection';
import type { CarePlanCertificationPeriod } from '../../../dtos/patientmr/careplans/careplan.dto';
import HomeboundSection, { homeboundJustificationField } from './sections/HomeboundSection';
import MedicalNecessitySection from './sections/MedicalNecessitySection';
import ProviderSection from './sections/ProviderSection';
import type { SectionFieldConfig } from './sections/SectionCard';
import { CarePlanGoal, initialGoals, clearedLegacyGoalFields, goalErrors } from './goals/carePlanGoals';
import { AssessmentsService, AssessmentListItem, AssessmentDiscipline } from '../../../services/patientmr/assessments.service';

interface PTCarePlanFormProps {
  editingId?: string | null;
  patientId?: string;
  existingData?: any;
  onSave: (data: any, submitAfterSave?: boolean) => void;
  onSubmit?: () => void;
  onReopen?: () => void;
  onApprove?: () => void;
  onCancel: () => void;
  saving?: boolean;
  submitting?: boolean;
  reopening?: boolean;
  carePlanStatus?: CarePlanStatus;
  isReadOnly?: boolean;
  /** Start QA review (SUBMITTED -> QA); shown only when provided */
  onStartQA?: () => void;
  startingQA?: boolean;
  /** Extra header content (version badge, version picker) */
  headerExtra?: React.ReactNode;
  /** Saves a draft without closing the form (autosave) */
  onAutosave?: (data: Record<string, unknown>) => Promise<void>;
  /** Autosave runs only while true */
  autosaveEnabled?: boolean;
  /** Phase 19 F26: the plan record's certification period; its dates are shown read-only */
  certificationPeriod?: CarePlanCertificationPeriod | null;
}

interface CheckboxFieldProps {
  field: string;
  label: string;
  checked: boolean;
  onChange: (field: string, value: boolean) => void;
  isReadOnly?: boolean;
}

const CheckboxField = React.memo(({ field, label, checked, onChange, isReadOnly = false }: CheckboxFieldProps) => (
  <label className={`flex items-center space-x-2 text-sm text-gray-700 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(field, e.target.checked)}
      disabled={isReadOnly}
      className={`rounded border-gray-300 text-green-600 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
    />
    <span>{label}</span>
  </label>
));

// Phase 19 F25: per-plan copy for the shared sections (keys unchanged).
const PT_PHYSICIAN_OVERRIDES = {
  orderingPhysicianName: { label: 'Physician Name', placeholder: 'Enter ordering physician name' },
  orderingPhysicianNPI: { label: 'NPI Number' },
  orderingPhysicianPhone: { label: 'Phone Number', placeholder: '(555) 555-5555' },
  orderingPhysicianSpecialty: { placeholder: 'e.g., Pediatrician, Physiatrist' },
  orderingPhysicianFax: { label: 'Fax Number', placeholder: '(555) 555-5556' },
  orderingPhysicianAddressStreet: { label: 'Address', placeholder: 'Street address' },
};

const PT_MEDICAL_NECESSITY_GUIDANCE = {
  heading: 'Must include for PT:',
  items: [
    'Clinical complexity (diagnoses, functional deficits, comorbidities)',
    'Skilled PT requirements (specific techniques requiring therapist expertise, not aide)',
    'Functional impact (how impairments affect mobility, ADLs, safety)',
    'Expected outcomes (functional improvements, timeframe, prevention of complications)',
    'Why home health setting (if applicable - unable to leave home, equipment needs)',
  ],
};

const PT_MEDICAL_NECESSITY_FIELDS: SectionFieldConfig[] = [
  { key: 'medicalNecessityJustification', label: 'Medical necessity justification', srOnlyLabel: true, kind: 'textarea', rows: 8, placeholder: 'This patient requires skilled physical therapy services due to...' },
];

const PT_HOMEBOUND_FIELDS: SectionFieldConfig[] = [
  { key: 'homeboundIndicators', label: 'Homebound Indicators (Check all that apply)', kind: 'checkboxes', items: [
    { key: 'homeboundLeavesHomeInfrequently', label: 'Patient leaves home infrequently due to medical condition' },
    { key: 'homeboundRequiresConsiderableEffort', label: 'Leaving home requires considerable and taxing effort' },
    { key: 'homeboundRequiresSupportiveDevices', label: 'Requires supportive devices (wheelchair, walker, ventilator, oxygen)' },
    { key: 'homeboundRequiresSpecialTransport', label: 'Requires special medical transportation' },
    { key: 'homeboundRequiresAssistance', label: 'Requires assistance of another person to leave home' },
    { key: 'homeboundNormalInability', label: 'Normal inability to leave home' },
  ] },
  homeboundJustificationField({ required: true, placeholder: 'Explain why patient is confined to home for PT purposes. Include: mobility limitations, equipment dependencies, medical fragility, effort/risk of leaving home.' }),
];

const PT_PROVIDER_OVERRIDES = {
  providerName: { placeholder: 'Enter PT name' },
  providerCredentials: { placeholder: 'e.g., PT, DPT, PCS' },
  providerNPI: { maxLength: 10 },
};

const PTCarePlanForm: React.FC<PTCarePlanFormProps> = ({
  editingId,
  patientId,
  existingData,
  onSave,
  onReopen,
  onApprove,
  onCancel,
  saving = false,
  submitting = false,
  reopening = false,
  carePlanStatus = CarePlanStatus.DRAFT,
  isReadOnly = false,
  onStartQA,
  startingQA = false,
  headerExtra,
  onAutosave,
  autosaveEnabled = false,
  certificationPeriod = null
}) => {
  const [formConfig, setFormConfig] = useState({
    patientType: 'pediatric' as 'pediatric' | 'adult',
    settingType: 'home-health' as 'home-health' | 'outpatient',
    isPDN: true,
  });

  const [formData, setFormData] = useState({
    // Phase 19 F12: every goal of the plan (domain, measure, target date, status, progress)
    goals: [] as CarePlanGoal[],
    // === ASSESSMENT REFERENCE ===
    assessmentId: '',
    assessmentDate: '',
    assessedBy: '',
    assessmentType: '',
    
    // === CERTIFICATION PERIOD ===
    certificationPeriodFrom: '',
    certificationPeriodTo: '',
    
    // === ORDERING PHYSICIAN ===
    orderingPhysicianName: '',
    orderingPhysicianNPI: '',
    orderingPhysicianPhone: '',
    orderingPhysicianFax: '',
    orderingPhysicianSpecialty: '',
    orderingPhysicianAddressStreet: '',
    orderingPhysicianAddressCity: '',
    orderingPhysicianAddressState: '',
    orderingPhysicianAddressZip: '',
    physicianReceivedPOCDate: '',
    
    // === CLINICAL STATUS ===
    prognosis: '',
    rehabilitationPotential: '',
    currentPainLevel: '',
    painLocation: '',
    
    // === MEDICAL NECESSITY ===
    medicalNecessityJustification: '',
    
    // === HOMEBOUND STATUS (CHECKBOXES) ===
    homeboundLeavesHomeInfrequently: false,
    homeboundRequiresConsiderableEffort: false,
    homeboundRequiresSupportiveDevices: false,
    homeboundRequiresSpecialTransport: false,
    homeboundRequiresAssistance: false,
    homeboundNormalInability: false,
    homeboundJustification: '',
    
    // === FUNCTIONAL LIMITATIONS ===
    functionalLimitationAmbulation: false,
    functionalLimitationTransfers: false,
    functionalLimitationBedMobility: false,
    functionalLimitationBalance: false,
    functionalLimitationStrength: false,
    functionalLimitationEndurance: false,
    functionalLimitationROM: false,
    functionalLimitationCoordination: false,
    functionalLimitationOther: '',
    
    // === ACTIVITIES PERMITTED ===
    activityBedrest: false,
    activityBRP: false,
    activityUpAsToleratedWithAssist: false,
    activityUpAsTolerated: false,
    activityPartialWeightBearing: false,
    activityNonWeightBearing: false,
    activityIndependentAtHome: false,
    activityCrutchesWalkerCane: false,
    activityWheelchair: false,
    activityOtherActivity: '',
    activityRestrictionsNarrative: '',
    
    // === SAFETY PRECAUTIONS ===
    safetyFallPrecautions: false,
    safetySeizurePrecautions: false,
    safetyAspirationPrecautions: false,
    safetyWeightBearingRestrictions: false,
    safetyWeightBearingStatus: '',
    safetyMovementPrecautions: '',
    safetyOxygenSafety: false,
    safetySupervisedAtAllTimes: false,
    safetyPositioningProtocol: false,
    safetyOther: '',
    
    // === PEDIATRIC DEVELOPMENTAL STATUS ===
    pediatricChronologicalAge: '',
    pediatricAdjustedAge: '',
    pediatricGrossMotorAge: '',
    pediatricGMFCSLevel: '',
    pediatricDevelopmentalMilestones: '',
    
    // === PT GOALS - SHORT TERM (60-90 days) ===
    goalMobilityShortTerm: '',
    goalMobilityShortTermTargetDate: '',
    goalMobilityShortTermMeasurable: '',
    
    goalStrengthShortTerm: '',
    goalStrengthShortTermTargetDate: '',
    goalStrengthShortTermMeasurable: '',
    
    goalBalanceShortTerm: '',
    goalBalanceShortTermTargetDate: '',
    goalBalanceShortTermMeasurable: '',
    
    // === PT GOALS - LONG TERM (120-180 days) ===
    goalMobilityLongTerm: '',
    goalMobilityLongTermTargetDate: '',
    goalMobilityLongTermMeasurable: '',
    
    goalStrengthLongTerm: '',
    goalStrengthLongTermTargetDate: '',
    goalStrengthLongTermMeasurable: '',
    
    goalFunctionalMobilityLongTerm: '',
    goalFunctionalMobilityLongTermTargetDate: '',
    goalFunctionalMobilityLongTermMeasurable: '',
    
    goalCaregiverEducation: '',
    goalCaregiverEducationTargetDate: '',
    goalCaregiverEducationMeasurable: '',
    
    // === PT INTERVENTIONS (ADULT/GENERAL) ===
    interventionTherapeuticExercise: '',
    interventionNeuromuscularReeducation: '',
    interventionGaitTraining: '',
    interventionBalanceTraining: '',
    interventionFunctionalMobilityTraining: '',
    interventionPositioning: '',
    interventionCaregiverTraining: '',
    interventionHomeExerciseProgram: '',
    
    // === PEDIATRIC PT INTERVENTIONS ===
    pediatricNDTTechniques: '',
    pediatricDevelopmentalActivities: '',
    pediatricWeightBearingActivities: '',
    pediatricPlayBasedTherapy: '',
    pediatricGrossMotorProgression: '',
    pediatricSensorimotorIntegration: '',
    pediatricPositioningProgram: '',
    pediatricEquipmentTraining: '',
    pediatricROMExercises: '',
    pediatricStrengtheningActivities: '',
    pediatricMobilityTraining: '',
    pediatricBalanceCoordination: '',
    pediatricHomeEnvironmentMods: '',
    pediatricPositioningSchedule: '',
    pediatricHEP: '',
    pediatricCaregiverHandling: '',
    pediatricPlayRecommendations: '',
    pediatricSafetyEducation: '',
    pediatricCoordinationNursing: '',
    pediatricCoordinationOT: '',
    pediatricCoordinationST: '',
    pediatricCoordinationSchoolECI: '',
    pediatricOutcomeMeasures: '',
    pediatricReassessmentSchedule: '',
    
    // === PT FREQUENCY AND DURATION ===
    ptFrequency: '',
    ptFrequencyCustom: '',
    ptDuration: '',
    ptSessionLength: '',
    ptFrequencyJustification: '',
    
    // === PDN PT HOURS ===
    pdnPTHoursPerWeek: '',
    pdnPTSessionsPerWeek: '',
    pdnPTTotalWeeklyHours: '',
    
    // === DME AND EQUIPMENT (CONSOLIDATED) ===
    dmeWheelchair: false,
    dmeWheelchairType: '',
    dmeWalker: false,
    dmeWalkerType: '',
    dmeGaitTrainer: false,
    dmeGaitTrainerType: '',
    dmeCrutches: false,
    dmeCane: false,
    dmeStander: false,
    dmeStanderType: '',
    dmeStanderSchedule: '',
    dmeAFO: false,
    dmeAFOType: '',
    dmeAFOWearingSchedule: '',
    dmeKAFO: false,
    dmeAdaptiveSeating: false,
    dmeAdaptiveSeatingDetails: '',
    dmeOther: '',
    
    // === PDN PT CONSIDERATIONS ===
    pdnTracheostomyStatus: '',
    pdnVentilatorStatus: '',
    pdnMedicalStabilityForPT: '',
    pdnPTSessionPrecautions: '',
    pdnOxygenRequirementsDuringPT: '',
    pdnEmergencyEquipmentAvailability: '',
    
    // === PDN CAREGIVER PT TRAINING ===
    pdnPTCaregiverName: '',
    pdnPTCaregiverTrainingStatus: '',
    pdnPTCaregiverCompetencies: '',
    pdnPTCaregiverTrainingPlan: '',
    
    // === PDN HIP SURVEILLANCE ===
    pdnHipSurveillanceRequired: false,
    pdnLastHipXray: '',
    pdnHipStatus: '',
    pdnHipPrecautions: '',
    
    // === DISCHARGE PLANNING ===
    dischargePlanning: '',
    dischargeGoals: '',
    anticipatedDischargeDate: '',
    pediatricSchoolTransitionPlan: '',
    equipmentAtDischarge: '',
    homeExerciseProgramAtDischarge: '',
    
    // === PROVIDER INFORMATION ===
    providerName: '',
    providerCredentials: '',
    providerNPI: '',
    dateCreated: new Date().toISOString().split('T')[0],
    
    // === ADDITIONAL NOTES ===
    additionalNotes: ''
  });

  const [assessmentsList, setAssessmentsList] = useState<AssessmentListItem[]>([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);
  const hasLoadedExistingData = useRef(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [configSectionCollapsed, setConfigSectionCollapsed] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    'Ordering Physician': true,
    'Clinical Status': true,
    'Medical Necessity': true,
    'Homebound Status': true,
    'Functional Status': true,
    'Pediatric Development': true,
    'PDN Considerations': true,
    'PT Goals': true,
    'PT Interventions': true,
    'Pediatric PT Interventions': true,
    'PT Frequency': true,
    'DME Equipment': true,
    'PDN Caregiver Training': true,
    'PDN Hip Surveillance': true,
    'Discharge Planning': true,
    'Provider Information': true,
    'Additional Notes': true
  });
  const allSectionsCollapsed = useMemo(
    () => Object.values(collapsedSections).every(v => v === true),
    [collapsedSections]
  );

  const toggleSection = useCallback((section: string) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const toggleAllSections = useCallback((collapse: boolean) => {
    const newState: any = {};
    Object.keys(collapsedSections).forEach(key => { newState[key] = collapse; });
    setCollapsedSections(newState);
    setConfigSectionCollapsed(collapse);
  }, [collapsedSections]);

  useEffect(() => {
    if (existingData && !hasLoadedExistingData.current) {
      setFormData(prev => ({ ...prev, ...existingData, goals: initialGoals('PT', existingData) }));
      if (existingData.formConfig) {
        setFormConfig(existingData.formConfig);
      }
      hasLoadedExistingData.current = true;
    }
  }, [existingData]);

  // Phase 19 F26: a plan with a certification period stores its dates in the cert period keys
  useEffect(() => {
    if (hasFixedCertPeriod(certificationPeriod)) setFormData(prev => applyCertPeriod(prev, certificationPeriod));
  }, [certificationPeriod]);

  // Auto-calculate total weekly PT hours
  useEffect(() => {
    const hoursPerSession = parseFloat(formData.pdnPTHoursPerWeek) || 0;
    const sessionsPerWeek = parseFloat(formData.pdnPTSessionsPerWeek) || 0;
    const total = hoursPerSession * sessionsPerWeek;
    if (total > 0) {
      setFormData(prev => ({ ...prev, pdnPTTotalWeeklyHours: total.toFixed(1) }));
    }
  }, [formData.pdnPTHoursPerWeek, formData.pdnPTSessionsPerWeek]);

  const handleConfigChange = (key: string, value: boolean | string) => {
    setFormConfig(prev => ({ ...prev, [key]: value }));
  };

  const loadAssessments = async () => {
    if (assessmentsList.length > 0) return;
    
    try {
      setLoadingAssessments(true);
      const result = await AssessmentsService.getAssessments(patientId);
      // Filter based on discipline: PT
      const disciplineAssessments = result.assessments.filter(
        assessment => assessment.discipline === AssessmentDiscipline.PT
      );
      setAssessmentsList(disciplineAssessments);
    } catch (error) {
      console.error('Error loading assessments:', error);
    } finally {
      setLoadingAssessments(false);
    }
  };

  const formatAssessmentType = (assessmentType: string | undefined): string => {
    if (!assessmentType) return 'N/A';
    
    const typeMap: Record<string, string> = {
      'START_OF_CARE': 'Start of Care',
      'INITIAL': 'Start of Care',
      'initial': 'Start of Care',
      'REASSESSMENT': 'Recertification',
      'REASSESSMEMT': 'Recertification',
      'recertification': 'Recertification',
      'END_OF_CARE': 'Discharge',
      'discharge': 'Discharge'
    };
    
    return typeMap[assessmentType] || assessmentType;
  };

  const handleAssessmentSelection = (assessmentId: string) => {
    const selectedAssessment = assessmentsList.find(a => a.assessmentId === assessmentId);
    
    if (selectedAssessment) {
      setFormData(prev => ({
        ...prev,
        assessmentId: assessmentId,
        assessmentDate: selectedAssessment.date ? new Date(selectedAssessment.date).toISOString().split('T')[0] : '',
        assessedBy: selectedAssessment.providerName || '',
        assessmentType: selectedAssessment.assessmentType || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        assessmentId: assessmentId,
        assessmentDate: '',
        assessedBy: '',
        assessmentType: ''
      }));
    }
  };

  // getStatusColor / getStatusDisplayText removed — use <DocStatusPill /> from common/Pills

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    Object.assign(newErrors, goalErrors(formData.goals));
    
    if (!formData.assessmentId) newErrors.assessmentId = 'Assessment reference is required';
    if (!formData.certificationPeriodFrom) newErrors.certificationPeriodFrom = 'Certification start date is required';
    if (!formData.certificationPeriodTo) newErrors.certificationPeriodTo = 'Certification end date is required';
    if (!formData.orderingPhysicianName.trim()) newErrors.orderingPhysicianName = 'Ordering physician name is required';
    if (!formData.orderingPhysicianNPI.trim()) newErrors.orderingPhysicianNPI = 'Physician NPI required for CMS-485';
    if (!formData.prognosis) newErrors.prognosis = 'Prognosis is required';
    if (!formData.rehabilitationPotential) newErrors.rehabilitationPotential = 'Rehabilitation potential is required';
    
    if (!formData.medicalNecessityJustification.trim()) {
      newErrors.medicalNecessityJustification = 'Medical necessity justification required for PA/CMS-485';
    }
    
    if (formConfig.settingType === 'home-health') {
      if (!formData.homeboundJustification.trim()) {
        newErrors.homeboundJustification = 'Homebound justification required for CMS-485';
      }
    }
    
    
    if (!formData.interventionTherapeuticExercise.trim() && !formData.interventionGaitTraining.trim()) {
      newErrors.interventionTherapeuticExercise = 'At least one PT intervention is required';
    }
    
    if (!formData.ptFrequency) newErrors.ptFrequency = 'Visit frequency is required';
    if (!formData.ptDuration) newErrors.ptDuration = 'Treatment duration is required';
    if (!formData.providerName.trim()) newErrors.providerName = 'Provider name is required';
    
    if (formConfig.isPDN) {
      if (!formData.pdnMedicalStabilityForPT) {
        newErrors.pdnMedicalStabilityForPT = 'Medical stability for PT is required for PDN';
      }
      if (!formData.ptFrequencyJustification.trim()) {
        newErrors.ptFrequencyJustification = 'Frequency justification required for Medicaid PDN';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = (submitAfterSave = false) => {
    // Phase 4: Save keeps a DRAFT without required-field checks; Submit validates first
    if (!submitAfterSave || validateForm()) {
      onSave({ ...formData, ...clearedLegacyGoalFields('PT'), goals: formData.goals, formConfig, status: 'Draft' }, submitAfterSave);
    }
  };

  const handleChange = useCallback((field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: '' };
    });
  }, []);

  const isPediatric = formConfig.patientType === 'pediatric';
  const isPediatricPDN = formConfig.patientType === 'pediatric' && formConfig.isPDN;
  const isHomeHealth = formConfig.settingType === 'home-health';

  const isAnyLoading = saving || submitting || reopening || startingQA;
  const isDraft = !carePlanStatus || carePlanStatus === CarePlanStatus.DRAFT;
  const isSubmittedOrQA = carePlanStatus === CarePlanStatus.SUBMITTED || carePlanStatus === CarePlanStatus.QA;

  // Phase 4 (ASM-03): dirty tracking, autosave while DRAFT, unsaved-changes guard
  const draft = useCarePlanDraft({
    getSnapshot: () => JSON.stringify({ formData, formConfig }),
    getSaveData: () => ({ ...formData, ...clearedLegacyGoalFields('PT'), goals: formData.goals, formConfig, status: 'Draft' }),
    onAutosave,
    enabled: autosaveEnabled && isDraft && !isReadOnly && !!editingId,
    resetKey: `${editingId ?? 'new'}:${carePlanStatus}`,
  });
  const handleClose = () => draft.confirmLeave(onCancel);
  const isComplete = carePlanStatus === CarePlanStatus.COMPLETE;

  const actions: ActionButtonConfig[] = [
    { key: 'cancel', label: 'Close', onClick: handleClose, variant: 'secondary', disabled: isAnyLoading },
    { key: 'save', label: 'Save', loadingLabel: editingId ? 'Updating...' : 'Saving...', onClick: () => handleSave(false), variant: 'primary', disabled: isAnyLoading, loading: saving, hidden: !isDraft || isReadOnly },
    { key: 'submit', label: 'Submit', loadingLabel: 'Submitting...', onClick: () => handleSave(true), variant: 'primary', disabled: isAnyLoading, loading: submitting, hidden: !isDraft || isReadOnly },
    { key: 'reject', label: 'Reject', loadingLabel: 'Rejecting...', onClick: onReopen ?? (() => {}), variant: 'danger', disabled: isAnyLoading, loading: reopening, hidden: !isSubmittedOrQA || !onReopen || isReadOnly },
    { key: 'startQA', label: 'Start QA', loadingLabel: 'Starting QA...', onClick: onStartQA ?? (() => {}), variant: 'warning', disabled: isAnyLoading, loading: startingQA, hidden: carePlanStatus !== CarePlanStatus.SUBMITTED || !onStartQA || isReadOnly },
    { key: 'approve', label: 'Approve', loadingLabel: 'Approving...', onClick: onApprove ?? (() => {}), variant: 'success', disabled: isAnyLoading, loading: submitting, hidden: !isSubmittedOrQA || !onApprove || isReadOnly },
    { key: 'reopen', label: 'Reopen', loadingLabel: 'Reopening...', onClick: onReopen ?? (() => {}), variant: 'primary', disabled: isAnyLoading, loading: reopening, hidden: !isComplete || !onReopen || isReadOnly, className: 'bg-blue-600 hover:bg-blue-700' },
  ];

  return (
    <FormPageLayout
      onBack={handleClose}
      actions={actions}
      title="Physical Therapy Care Plan"
      headerRight={
        <div className="flex items-center gap-2">
          {draft.indicator}
          {headerExtra}
          <DocStatusPill status={carePlanStatus} />
          <button
            type="button"
            onClick={() => toggleAllSections(!allSectionsCollapsed)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md border border-slate-200 transition-colors"
          >
            {allSectionsCollapsed ? (
              <><ChevronsDown size={14} /><span>Expand All</span></>
            ) : (
              <><ChevronsUp size={14} /><span>Collapse All</span></>
            )}
          </button>
        </div>
      }
    >
      <div {...draft.captureProps}>
      <div className="relative">
        <div className="bg-gray-50 rounded-lg border border-gray-200 text-left">
          <div
            className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-100 rounded-t-lg transition-colors"
            onClick={() => setConfigSectionCollapsed(!configSectionCollapsed)}
          >
            <div className="flex items-center gap-3">
              <FileEdit size={18} className="text-blue-600" />
              <div>
                <h4 className="text-md font-medium text-gray-700">Care Plan Configuration</h4>
                {configSectionCollapsed && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {formConfig.patientType.charAt(0).toUpperCase() + formConfig.patientType.slice(1)} • 
                    {formConfig.settingType === 'home-health' ? ' Home Health' : ' Outpatient'} • 
                    {formConfig.isPDN ? ' PDN' : ' Standard'}
                  </p>
                )}
              </div>
            </div>
            <div>
              {configSectionCollapsed ? <ChevronDown size={20} className="text-gray-500" /> : <ChevronUp size={20} className="text-gray-500" />}
            </div>
          </div>

          <div className={configSectionCollapsed ? 'hidden' : 'p-4 border-t border-gray-200'}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Patient Type</label>
                <select className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                  disabled={isReadOnly}
                  value={formConfig.patientType}
                  onChange={(e) => handleConfigChange('patientType', e.target.value)}>
                  <option value="pediatric">Pediatric</option>
                  <option value="adult">Adult</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Setting</label>
                <select className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                  disabled={isReadOnly}
                  value={formConfig.settingType}
                  onChange={(e) => handleConfigChange('settingType', e.target.value)}>
                  <option value="home-health">Home Health</option>
                  <option value="outpatient">Outpatient</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center text-sm text-gray-600">
                  <input type="checkbox" className={`mr-2 rounded border-gray-300 text-green-600 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isReadOnly}
                    checked={formConfig.isPDN}
                    onChange={(e) => handleConfigChange('isPDN', e.target.checked)} />
                  PDN Patient (Private Duty Nursing)
                </label>
              </div>
            </div>

            {/* Assessment Reference */}
            <div className="mt-6">
              <h5 className="text-sm font-medium text-gray-700 mb-3">Assessment Reference</h5>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Physical Therapy Assessment *</label>
                  <select
                    ref={selectRef}
                    value={formData.assessmentId}
                    onChange={(e) => handleAssessmentSelection(e.target.value)}
                    onFocus={loadAssessments}
                    className={`w-full px-2 py-2 text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.assessmentId ? 'border-red-500' : 'border-gray-300'} max-w-full ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    disabled={loadingAssessments || isReadOnly}
                  >
                    <option value="">{loadingAssessments ? 'Loading PT assessments...' : 'Select a PT assessment'}</option>
                    {assessmentsList.map((assessment) => (
                      <option key={assessment.assessmentId} value={assessment.assessmentId}>
                        {formatAssessmentType(assessment.assessmentType)} | {new Date(assessment.date).toLocaleDateString()} | {assessment.providerName}
                      </option>
                    ))}
                  </select>
                  {errors.assessmentId && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.assessmentId}</p>}
                </div>
                
                {/* Selected Assessment Details */}
                {formData.assessmentId && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h6 className="text-sm font-semibold text-green-900 mb-2">Selected Assessment Details</h6>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Assessment ID:</span>
                        <p className="text-gray-900 mt-0.5">{formData.assessmentId}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Type:</span>
                        <p className="text-gray-900 mt-0.5">
                          {formatAssessmentType(formData.assessmentType)}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Assessment Date:</span>
                        <p className="text-gray-900 mt-0.5">
                          {formData.assessmentDate ? new Date(formData.assessmentDate).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Assessed By:</span>
                        <p className="text-gray-900 mt-0.5">{formData.assessedBy || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Certification Period */}
            <CertPeriodSection fixedPeriod={certificationPeriod} values={formData} onChange={handleChange} errors={errors} readOnly={isReadOnly} />

            {isPediatricPDN && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start">
                <Activity size={18} className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-green-800">
                  <strong>Pediatric PDN PT Care Plan:</strong> Includes gross motor development, mobility equipment, hip surveillance, and caregiver training sections required for Texas Medicaid PDN authorization.
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6 text-left">
          {/* ========== ORDERING PHYSICIAN ========== */}
          <OrderingPhysicianSection
            title="Ordering Physician (Required for CMS-485)"
            overrides={PT_PHYSICIAN_OVERRIDES}
            showReceivedPOCDate
            values={formData}
            onChange={handleChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={collapsedSections['Ordering Physician']}
            onToggle={() => toggleSection('Ordering Physician')}
          />

          {/* ========== CLINICAL STATUS ========== */}
          <div className="bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-center justify-between cursor-pointer p-4 hover:bg-amber-100 rounded-t-lg transition-colors"
              onClick={() => toggleSection('Clinical Status')}>
              <div className="flex items-center gap-3">
                <Activity size={18} className="text-amber-600" />
                <div>
                  <h4 className="text-md font-medium text-amber-900">Clinical Status (CMS-485 Section 11)</h4>
                </div>
              </div>
              <div>
                {collapsedSections['Clinical Status'] ? <ChevronDown size={20} className="text-amber-600" /> : <ChevronUp size={20} className="text-amber-600" />}
              </div>
            </div>

            <div className={collapsedSections['Clinical Status'] ? 'hidden' : 'p-4 border-t border-amber-200'}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prognosis *</label>
                  <select disabled={isReadOnly} value={formData.prognosis} onChange={(e) => handleChange('prognosis', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.prognosis ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select prognosis</option>
                    <option value="Poor">Poor</option>
                    <option value="Guarded">Guarded</option>
                    <option value="Fair">Fair</option>
                    <option value="Good">Good</option>
                    <option value="Excellent">Excellent</option>
                  </select>
                  {errors.prognosis && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.prognosis}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rehabilitation Potential *</label>
                  <select disabled={isReadOnly} value={formData.rehabilitationPotential} onChange={(e) => handleChange('rehabilitationPotential', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.rehabilitationPotential ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select potential</option>
                    <option value="None">None</option>
                    <option value="Minimal">Minimal</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Good">Good</option>
                    <option value="Excellent">Excellent</option>
                  </select>
                  {errors.rehabilitationPotential && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.rehabilitationPotential}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Pain Level (0-10)</label>
                  <input type="text" disabled={isReadOnly} value={formData.currentPainLevel}
                    onChange={(e) => handleChange('currentPainLevel', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="e.g., 4/10" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pain Location</label>
                  <input type="text" disabled={isReadOnly} value={formData.painLocation}
                    onChange={(e) => handleChange('painLocation', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="e.g., Bilateral lower extremities" />
                </div>
              </div>
            </div>
          </div>

          {/* ========== MEDICAL NECESSITY ========== */}
          <MedicalNecessitySection
            title="Medical Necessity Justification (Required for PA & CMS-485)"
            guidance={PT_MEDICAL_NECESSITY_GUIDANCE}
            fields={PT_MEDICAL_NECESSITY_FIELDS}
            values={formData}
            onChange={handleChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={collapsedSections['Medical Necessity']}
            onToggle={() => toggleSection('Medical Necessity')}
          />

          {/* ========== HOMEBOUND STATUS ========== */}
          {isHomeHealth && (
            <HomeboundSection
              title="Homebound Status (Required for CMS-485 Section 15)"
              fields={PT_HOMEBOUND_FIELDS}
              values={formData}
              onChange={handleChange}
              errors={errors}
              readOnly={isReadOnly}
              collapsed={collapsedSections['Homebound Status']}
              onToggle={() => toggleSection('Homebound Status')}
            />
          )}

          {/* ========== FUNCTIONAL STATUS & ACTIVITY LEVEL ========== */}
          <div className="bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between cursor-pointer p-4 hover:bg-blue-100 rounded-t-lg transition-colors"
              onClick={() => toggleSection('Functional Status')}>
              <div className="flex items-center gap-3">
                <Activity size={18} className="text-blue-600" />
                <div>
                  <h4 className="text-md font-medium text-blue-900">Functional Status & Activity Level (CMS-485 Section 8 & 9)</h4>
                </div>
              </div>
              <div>
                {collapsedSections['Functional Status'] ? <ChevronDown size={20} className="text-blue-600" /> : <ChevronUp size={20} className="text-blue-600" />}
              </div>
            </div>

            <div className={collapsedSections['Functional Status'] ? 'hidden' : 'p-4 border-t border-blue-200'}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Functional Limitations</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <CheckboxField field="functionalLimitationAmbulation" label="Ambulation" checked={formData.functionalLimitationAmbulation} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="functionalLimitationTransfers" label="Transfers" checked={formData.functionalLimitationTransfers} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="functionalLimitationBedMobility" label="Bed Mobility" checked={formData.functionalLimitationBedMobility} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="functionalLimitationBalance" label="Balance" checked={formData.functionalLimitationBalance} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="functionalLimitationStrength" label="Strength" checked={formData.functionalLimitationStrength} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="functionalLimitationEndurance" label="Endurance" checked={formData.functionalLimitationEndurance} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="functionalLimitationROM" label="Range of Motion" checked={formData.functionalLimitationROM} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="functionalLimitationCoordination" label="Coordination" checked={formData.functionalLimitationCoordination} onChange={handleChange}  isReadOnly={isReadOnly} />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Functional Limitations</label>
                  <input type="text" disabled={isReadOnly} value={formData.functionalLimitationOther}
                    onChange={(e) => handleChange('functionalLimitationOther', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="Describe other limitations" />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Activities Permitted</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <CheckboxField field="activityBedrest" label="Bedrest" checked={formData.activityBedrest} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="activityBRP" label="BRP" checked={formData.activityBRP} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="activityUpAsToleratedWithAssist" label="Up w/ Assist" checked={formData.activityUpAsToleratedWithAssist} onChange={handleChange} />
                  <CheckboxField field="activityUpAsTolerated" label="Up as Tolerated" checked={formData.activityUpAsTolerated} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="activityPartialWeightBearing" label="Partial WB" checked={formData.activityPartialWeightBearing} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="activityNonWeightBearing" label="Non-WB" checked={formData.activityNonWeightBearing} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="activityIndependentAtHome" label="Independent at Home" checked={formData.activityIndependentAtHome} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="activityCrutchesWalkerCane" label="Crutches/Walker/Cane" checked={formData.activityCrutchesWalkerCane} onChange={handleChange} />
                  <CheckboxField field="activityWheelchair" label="Wheelchair" checked={formData.activityWheelchair} onChange={handleChange}  isReadOnly={isReadOnly} />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Activities</label>
                  <input type="text" disabled={isReadOnly} value={formData.activityOtherActivity}
                    onChange={(e) => handleChange('activityOtherActivity', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="Additional activity restrictions or permissions" />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Activity Restrictions & Instructions</label>
                  <textarea value={formData.activityRestrictionsNarrative}
                    onChange={(e) => handleChange('activityRestrictionsNarrative', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={2}
                    placeholder="Describe specific activity restrictions, weight-bearing status, positioning requirements" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Safety Precautions</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <CheckboxField field="safetyFallPrecautions" label="Fall Precautions" checked={formData.safetyFallPrecautions} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="safetySeizurePrecautions" label="Seizure Precautions" checked={formData.safetySeizurePrecautions} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="safetyAspirationPrecautions" label="Aspiration Precautions" checked={formData.safetyAspirationPrecautions} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="safetyWeightBearingRestrictions" label="WB Restrictions" checked={formData.safetyWeightBearingRestrictions} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="safetyOxygenSafety" label="Oxygen Safety" checked={formData.safetyOxygenSafety} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="safetySupervisedAtAllTimes" label="Supervised at All Times" checked={formData.safetySupervisedAtAllTimes} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="safetyPositioningProtocol" label="Positioning Protocol" checked={formData.safetyPositioningProtocol} onChange={handleChange}  isReadOnly={isReadOnly} />
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Weight-Bearing Status</label>
                    <input type="text" disabled={isReadOnly} value={formData.safetyWeightBearingStatus}
                      onChange={(e) => handleChange('safetyWeightBearingStatus', e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      placeholder="e.g., PWB 50% right LE, WBAT left LE" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Movement Precautions</label>
                    <input type="text" disabled={isReadOnly} value={formData.safetyMovementPrecautions}
                      onChange={(e) => handleChange('safetyMovementPrecautions', e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      placeholder="e.g., Hip precautions, no PROM shoulder >90°" />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Precautions</label>
                  <input type="text" disabled={isReadOnly} value={formData.safetyOther}
                    onChange={(e) => handleChange('safetyOther', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="Additional safety considerations" />
                </div>
              </div>
            </div>
          </div>
          {/* ========== PEDIATRIC DEVELOPMENTAL STATUS ========== */}
          {isPediatric && (
            <div className="bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between cursor-pointer p-4 hover:bg-purple-100 rounded-t-lg transition-colors"
                onClick={() => toggleSection('Pediatric Development')}>
                <div className="flex items-center gap-3">
                  <Baby size={18} className="text-purple-600" />
                  <div>
                    <h4 className="text-md font-medium text-purple-900">Pediatric Developmental Motor Status</h4>
                  </div>
                </div>
                <div>
                  {collapsedSections['Pediatric Development'] ? <ChevronDown size={20} className="text-purple-600" /> : <ChevronUp size={20} className="text-purple-600" />}
                </div>
              </div>

              <div className={collapsedSections['Pediatric Development'] ? 'hidden' : 'p-4 border-t border-purple-200'}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Chronological Age</label>
                    <input type="text" value={formData.pediatricChronologicalAge}
                      onChange={(e) => handleChange('pediatricChronologicalAge', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      placeholder="e.g., 2 years 4 months" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Adjusted Age</label>
                    <input type="text" value={formData.pediatricAdjustedAge}
                      onChange={(e) => handleChange('pediatricAdjustedAge', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      placeholder="e.g., 2 years 2 months" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gross Motor Age</label>
                    <input type="text" value={formData.pediatricGrossMotorAge}
                      onChange={(e) => handleChange('pediatricGrossMotorAge', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      placeholder="e.g., 6-9 months" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GMFCS Level</label>
                    <select disabled={isReadOnly} value={formData.pediatricGMFCSLevel}
                      onChange={(e) => handleChange('pediatricGMFCSLevel', e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select level</option>
                      <option value="Level I">Level I</option>
                      <option value="Level II">Level II</option>
                      <option value="Level III">Level III</option>
                      <option value="Level IV">Level IV</option>
                      <option value="Level V">Level V</option>
                      <option value="N/A">N/A</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Developmental Milestones</label>
                  <textarea value={formData.pediatricDevelopmentalMilestones}
                    onChange={(e) => handleChange('pediatricDevelopmentalMilestones', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={3}
                    placeholder="e.g., Head control: emerging, Rolling: absent, Sitting: supported only" />
                </div>
              </div>
            </div>
          )}

          {/* ========== PDN PT CONSIDERATIONS ========== */}
          {isPediatricPDN && (
            <div className="bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center justify-between cursor-pointer p-4 hover:bg-red-100 rounded-t-lg transition-colors"
                onClick={() => toggleSection('PDN Considerations')}>
                <div className="flex items-center gap-3">
                  <Heart size={18} className="text-red-600" />
                  <div>
                    <h4 className="text-md font-medium text-red-900">PDN PT Considerations & Safety</h4>
                  </div>
                </div>
                <div>
                  {collapsedSections['PDN Considerations'] ? <ChevronDown size={20} className="text-red-600" /> : <ChevronUp size={20} className="text-red-600" />}
                </div>
              </div>

              <div className={collapsedSections['PDN Considerations'] ? 'hidden' : 'p-4 border-t border-red-200'}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tracheostomy Status</label>
                    <select disabled={isReadOnly} value={formData.pdnTracheostomyStatus}
                      onChange={(e) => handleChange('pdnTracheostomyStatus', e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select status</option>
                      <option value="No tracheostomy">No tracheostomy</option>
                      <option value="Tracheostomy present">Tracheostomy present</option>
                      <option value="Tracheostomy - speaking valve">Tracheostomy - speaking valve</option>
                      <option value="Recently decannulated">Recently decannulated</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ventilator Status</label>
                    <select disabled={isReadOnly} value={formData.pdnVentilatorStatus}
                      onChange={(e) => handleChange('pdnVentilatorStatus', e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select status</option>
                      <option value="No ventilator">No ventilator</option>
                      <option value="Ventilator - continuous">Ventilator - continuous</option>
                      <option value="Ventilator - nocturnal">Ventilator - nocturnal only</option>
                      <option value="Weaning in progress">Weaning in progress</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medical Stability for PT *</label>
                    <select value={formData.pdnMedicalStabilityForPT}
                      onChange={(e) => handleChange('pdnMedicalStabilityForPT', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.pdnMedicalStabilityForPT ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select status</option>
                      <option value="Medically stable - full PT">Medically stable - full PT activities</option>
                      <option value="Stable with precautions">Stable with precautions</option>
                      <option value="Medically fragile - bedside only">Medically fragile - bedside PT only</option>
                      <option value="Unstable - PT on hold">Unstable - PT on hold</option>
                    </select>
                    {errors.pdnMedicalStabilityForPT && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.pdnMedicalStabilityForPT}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">O2 Requirements During PT</label>
                    <input type="text" disabled={isReadOnly} value={formData.pdnOxygenRequirementsDuringPT}
                      onChange={(e) => handleChange('pdnOxygenRequirementsDuringPT', e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      placeholder="e.g., 2L NC during activity" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PT Session Precautions</label>
                    <textarea value={formData.pdnPTSessionPrecautions}
                      onChange={(e) => handleChange('pdnPTSessionPrecautions', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={2}
                      placeholder="e.g., Monitor SpO2 continuously, position for optimal airway" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Equipment Available</label>
                    <textarea value={formData.pdnEmergencyEquipmentAvailability}
                      onChange={(e) => handleChange('pdnEmergencyEquipmentAvailability', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={2}
                      placeholder="e.g., Ambu bag at bedside, suction available" />
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* ========== GOALS (Phase 19 F12: one structure for every discipline) ========== */}
          <GoalsEditor
            discipline="PT"
            goals={formData.goals}
            onChange={(goals) => setFormData(prev => ({ ...prev, goals }))}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={Boolean(collapsedSections['PT Goals'])}
            onToggle={() => toggleSection('PT Goals')}
          />

          {/* ========== PT INTERVENTIONS (ADULT/GENERAL) ========== */}
          <div className="bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between cursor-pointer p-4 hover:bg-slate-100 rounded-t-lg transition-colors"
              onClick={() => toggleSection('PT Interventions')}>
              <div className="flex items-center gap-3">
                <ClipboardList size={18} className="text-slate-600" />
                <div>
                  <h4 className="text-md font-medium text-slate-900">Physical Therapy Interventions (CMS-485 Section 12)</h4>
                </div>
              </div>
              <div>
                {collapsedSections['PT Interventions'] ? <ChevronDown size={20} className="text-slate-600" /> : <ChevronUp size={20} className="text-slate-600" />}
              </div>
            </div>

            <div className={collapsedSections['PT Interventions'] ? 'hidden' : 'p-4 border-t border-slate-200'}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Therapeutic Exercise *</label>
                  <textarea value={formData.interventionTherapeuticExercise}
                    onChange={(e) => handleChange('interventionTherapeuticExercise', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.interventionTherapeuticExercise ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={3}
                    placeholder={isPediatric 
                      ? "e.g., PROM/AROM all extremities, weight bearing activities, developmental strengthening"
                      : "e.g., Strengthening, stretching, ROM exercises for bilateral LEs"} />
                  {errors.interventionTherapeuticExercise && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.interventionTherapeuticExercise}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Neuromuscular Reeducation</label>
                  <textarea value={formData.interventionNeuromuscularReeducation}
                    onChange={(e) => handleChange('interventionNeuromuscularReeducation', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={3}
                    placeholder={isPediatric 
                      ? "e.g., NDT techniques, facilitation of movement patterns"
                      : "e.g., Balance training, coordination exercises, motor control"} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gait Training</label>
                  <textarea value={formData.interventionGaitTraining}
                    onChange={(e) => handleChange('interventionGaitTraining', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={3}
                    placeholder={isPediatric 
                      ? "e.g., Gait trainer training, supported walking, weight shift"
                      : "e.g., Gait pattern training with walker, assistive device training"} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Balance Training</label>
                  <textarea value={formData.interventionBalanceTraining}
                    onChange={(e) => handleChange('interventionBalanceTraining', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={3}
                    placeholder="e.g., Sitting balance activities, standing balance with support" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Functional Mobility Training</label>
                  <textarea value={formData.interventionFunctionalMobilityTraining}
                    onChange={(e) => handleChange('interventionFunctionalMobilityTraining', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={3}
                    placeholder={isPediatric 
                      ? "e.g., Bed mobility, rolling, transitions, wheelchair mobility"
                      : "e.g., Transfers, bed mobility, stairs training"} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Positioning</label>
                  <textarea value={formData.interventionPositioning}
                    onChange={(e) => handleChange('interventionPositioning', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={3}
                    placeholder="e.g., Stander program, sidelying positioning, prone positioning protocol" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Caregiver Training</label>
                  <textarea value={formData.interventionCaregiverTraining}
                    onChange={(e) => handleChange('interventionCaregiverTraining', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={3}
                    placeholder="e.g., HEP instruction, equipment use, safe handling techniques" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Home Exercise Program</label>
                  <textarea value={formData.interventionHomeExerciseProgram}
                    onChange={(e) => handleChange('interventionHomeExerciseProgram', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={3}
                    placeholder="Specific exercises for home program" />
                </div>
              </div>
            </div>
          </div>

          {/* ========== PEDIATRIC PT INTERVENTIONS ========== */}
          {isPediatric && (
            <div className="bg-indigo-50 rounded-lg border border-indigo-200">
              <div className="flex items-center justify-between cursor-pointer p-4 hover:bg-indigo-100 rounded-t-lg transition-colors"
                onClick={() => toggleSection('Pediatric PT Interventions')}>
                <div className="flex items-center gap-3">
                  <Baby size={18} className="text-indigo-600" />
                  <div>
                    <h4 className="text-md font-medium text-indigo-900">Pediatric PT Interventions</h4>
                  </div>
                </div>
                <div>
                  {collapsedSections['Pediatric PT Interventions'] ? <ChevronDown size={20} className="text-indigo-600" /> : <ChevronUp size={20} className="text-indigo-600" />}
                </div>
              </div>

              <div className={collapsedSections['Pediatric PT Interventions'] ? 'hidden' : 'p-4 border-t border-indigo-200'}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">NDT Techniques</label>
                    <textarea value={formData.pediatricNDTTechniques}
                      onChange={(e) => handleChange('pediatricNDTTechniques', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={3}
                      placeholder="e.g., NDT handling, key points of control, facilitation of movement patterns, inhibition of abnormal tone" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Developmental Activities</label>
                    <textarea value={formData.pediatricDevelopmentalActivities}
                      onChange={(e) => handleChange('pediatricDevelopmentalActivities', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={3}
                      placeholder="e.g., Tummy time, supported sitting, rolling activities, crawling preparation" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Weight-Bearing Activities</label>
                    <textarea value={formData.pediatricWeightBearingActivities}
                      onChange={(e) => handleChange('pediatricWeightBearingActivities', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={3}
                      placeholder="e.g., Stander program for bone density, supported standing, weight bearing in quadruped" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Play-Based Therapy</label>
                    <textarea value={formData.pediatricPlayBasedTherapy}
                      onChange={(e) => handleChange('pediatricPlayBasedTherapy', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={3}
                      placeholder="e.g., Reaching for toys for trunk rotation, ball play for dynamic sitting balance" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gross Motor Progression</label>
                    <textarea value={formData.pediatricGrossMotorProgression}
                      onChange={(e) => handleChange('pediatricGrossMotorProgression', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={3}
                      placeholder="e.g., Head control exercises, trunk strengthening, pre-gait activities" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sensorimotor Integration</label>
                    <textarea value={formData.pediatricSensorimotorIntegration}
                      onChange={(e) => handleChange('pediatricSensorimotorIntegration', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={3}
                      placeholder="e.g., Vestibular input via movement, proprioceptive input through weight-bearing" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Positioning Program</label>
                    <textarea value={formData.pediatricPositioningProgram}
                      onChange={(e) => handleChange('pediatricPositioningProgram', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={3}
                      placeholder="e.g., Supine with hip abduction wedge, prone for respiratory drainage, sidelying for play" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Equipment Training</label>
                    <textarea value={formData.pediatricEquipmentTraining}
                      onChange={(e) => handleChange('pediatricEquipmentTraining', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={3}
                      placeholder="e.g., Gait trainer progression, stander tolerance, wheelchair propulsion" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ROM Exercises</label>
                    <textarea value={formData.pediatricROMExercises}
                      onChange={(e) => handleChange('pediatricROMExercises', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={3}
                      placeholder="e.g., PROM to prevent contractures, AROM through play-based reaching, stretching program" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Strengthening Activities</label>
                    <textarea value={formData.pediatricStrengtheningActivities}
                      onChange={(e) => handleChange('pediatricStrengtheningActivities', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={3}
                      placeholder="e.g., Core strengthening in sitting, LE strengthening through sit-to-stand" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobility Training</label>
                    <textarea value={formData.pediatricMobilityTraining}
                      onChange={(e) => handleChange('pediatricMobilityTraining', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={3}
                      placeholder="e.g., Floor mobility (rolling, scooting, crawling), wheelchair skills, walker training" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Balance & Coordination</label>
                    <textarea value={formData.pediatricBalanceCoordination}
                      onChange={(e) => handleChange('pediatricBalanceCoordination', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={3}
                      placeholder="e.g., Sitting balance on therapy ball, reaching outside base of support, weight shifting" />
                  </div>
                </div>

                <div className="mt-6 p-4 bg-white rounded-lg border border-indigo-300">
                  <h5 className="text-sm font-semibold text-indigo-900 mb-3">Environmental & Caregiver Support</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Home Environment Recommendations</label>
                      <textarea value={formData.pediatricHomeEnvironmentMods}
                        onChange={(e) => handleChange('pediatricHomeEnvironmentMods', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        rows={2}
                        placeholder="e.g., Clear pathways for mobility devices, non-slip flooring, furniture height for transfers" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Positioning Schedule</label>
                      <textarea value={formData.pediatricPositioningSchedule}
                        onChange={(e) => handleChange('pediatricPositioningSchedule', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        rows={2}
                        placeholder="e.g., Stander 30 min 2x/day, prone wedge 15 min after meals" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Home Exercise Program (HEP)</label>
                      <textarea value={formData.pediatricHEP}
                        onChange={(e) => handleChange('pediatricHEP', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        rows={3}
                        placeholder="e.g., ROM exercises 2x/day, tummy time 3x/day, sitting practice during meals" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Caregiver Handling Techniques</label>
                      <textarea value={formData.pediatricCaregiverHandling}
                        onChange={(e) => handleChange('pediatricCaregiverHandling', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        rows={3}
                        placeholder="e.g., Proper lifting with equipment considerations, safe transfers, optimal carrying positions" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Play Recommendations</label>
                      <textarea value={formData.pediatricPlayRecommendations}
                        onChange={(e) => handleChange('pediatricPlayRecommendations', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        rows={2}
                        placeholder="e.g., Toys that encourage reaching and rolling, floor time for crawling, push toys" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Safety Education</label>
                      <textarea value={formData.pediatricSafetyEducation}
                        onChange={(e) => handleChange('pediatricSafetyEducation', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        rows={2}
                        placeholder="e.g., Fall prevention, seizure precautions during PT, equipment safety checks" />
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-white rounded-lg border border-indigo-300">
                  <h5 className="text-sm font-semibold text-indigo-900 mb-3">Interdisciplinary Coordination</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Coordination with Nursing/PDN</label>
                      <textarea value={formData.pediatricCoordinationNursing}
                        onChange={(e) => handleChange('pediatricCoordinationNursing', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        rows={2}
                        placeholder="e.g., PT activities for nursing carryover, stander use during nursing shift" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Coordination with OT</label>
                      <textarea value={formData.pediatricCoordinationOT}
                        onChange={(e) => handleChange('pediatricCoordinationOT', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        rows={2}
                        placeholder="e.g., Shared positioning goals, joint equipment recommendations" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Coordination with ST</label>
                      <textarea value={formData.pediatricCoordinationST}
                        onChange={(e) => handleChange('pediatricCoordinationST', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        rows={2}
                        placeholder="e.g., Optimal positioning for feeding/communication, trunk support needs" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">School/ECI Coordination</label>
                      <textarea value={formData.pediatricCoordinationSchoolECI}
                        onChange={(e) => handleChange('pediatricCoordinationSchoolECI', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        rows={2}
                        placeholder="e.g., Communication with school PT, IEP alignment, ECI transition planning" />
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-white rounded-lg border border-indigo-300">
                  <h5 className="text-sm font-semibold text-indigo-900 mb-3">Progress Monitoring</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Outcome Measures Used</label>
                      <textarea value={formData.pediatricOutcomeMeasures}
                        onChange={(e) => handleChange('pediatricOutcomeMeasures', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        rows={2}
                        placeholder="e.g., GMFM-66, PEDI-CAT, goniometry, MMT, timed trials" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reassessment Schedule</label>
                      <textarea value={formData.pediatricReassessmentSchedule}
                        onChange={(e) => handleChange('pediatricReassessmentSchedule', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        rows={2}
                        placeholder="e.g., Formal reassessment every 90 days, GMFCS every 6 months, ROM/strength monthly" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* ========== PT FREQUENCY AND DURATION ========== */}
          <div className="bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between cursor-pointer p-4 hover:bg-blue-100 rounded-t-lg transition-colors"
              onClick={() => toggleSection('PT Frequency')}>
              <div className="flex items-center gap-3">
                <Clock size={18} className="text-blue-600" />
                <div>
                  <h4 className="text-md font-medium text-blue-900">PT Frequency & Duration (CMS-485 Section 12)</h4>
                </div>
              </div>
              <div>
                {collapsedSections['PT Frequency'] ? <ChevronDown size={20} className="text-blue-600" /> : <ChevronUp size={20} className="text-blue-600" />}
              </div>
            </div>

            <div className={collapsedSections['PT Frequency'] ? 'hidden' : 'p-4 border-t border-blue-200'}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Visit Frequency *</label>
                  <select value={formData.ptFrequency} onChange={(e) => handleChange('ptFrequency', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.ptFrequency ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select frequency</option>
                    <option value="1x/week">1x/week</option>
                    <option value="2x/week">2x/week</option>
                    <option value="3x/week">3x/week</option>
                    <option value="1x/month">1x/month</option>
                    <option value="2x/month">2x/month</option>
                    <option value="Custom">Custom - specify below</option>
                  </select>
                  {errors.ptFrequency && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.ptFrequency}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custom Frequency</label>
                  <input type="text" value={formData.ptFrequencyCustom}
                    onChange={(e) => handleChange('ptFrequencyCustom', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    placeholder="e.g., 2x/week x 4 weeks, then 1x/week" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration *</label>
                  <select value={formData.ptDuration} onChange={(e) => handleChange('ptDuration', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.ptDuration ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select duration</option>
                    <option value="30 days">30 days</option>
                    <option value="60 days">60 days</option>
                    <option value="90 days">90 days</option>
                    <option value="6 months">6 months</option>
                    <option value="1 year">1 year</option>
                    <option value="Ongoing">Ongoing (recertify as needed)</option>
                  </select>
                  {errors.ptDuration && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.ptDuration}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Session Length</label>
                  <select disabled={isReadOnly} value={formData.ptSessionLength} onChange={(e) => handleChange('ptSessionLength', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select session length</option>
                    <option value="15 minutes">15 minutes</option>
                    <option value="30 minutes">30 minutes</option>
                    <option value="45 minutes">45 minutes</option>
                    <option value="60 minutes">60 minutes</option>
                  </select>
                </div>
              </div>

              {formConfig.isPDN && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h5 className="text-sm font-semibold text-blue-900 mb-3">PDN PT Hours (If applicable)</h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hours Per Session</label>
                      <input type="number" disabled={isReadOnly} value={formData.pdnPTHoursPerWeek}
                        onChange={(e) => handleChange('pdnPTHoursPerWeek', e.target.value)}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        placeholder="e.g., 1" step="0.5" min="0" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sessions Per Week</label>
                      <input type="number" disabled={isReadOnly} value={formData.pdnPTSessionsPerWeek}
                        onChange={(e) => handleChange('pdnPTSessionsPerWeek', e.target.value)}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        placeholder="e.g., 2" min="0" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Total Weekly Hours</label>
                      <input type="text" value={formData.pdnPTTotalWeeklyHours}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                        readOnly placeholder="Auto-calculated" />
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency Justification {formConfig.isPDN && '*'}</label>
                <textarea value={formData.ptFrequencyJustification}
                  onChange={(e) => handleChange('ptFrequencyJustification', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.ptFrequencyJustification ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                  rows={3}
                  placeholder="Justify medical necessity for requested PT frequency. Include: developmental delays, functional deficits, caregiver training needs, equipment requirements" />
                {errors.ptFrequencyJustification && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.ptFrequencyJustification}</p>}
              </div>
            </div>
          </div>

          {/* ========== DME & EQUIPMENT (CONSOLIDATED) ========== */}
          <div className="bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between cursor-pointer p-4 hover:bg-slate-100 rounded-t-lg transition-colors"
              onClick={() => toggleSection('DME Equipment')}>
              <div className="flex items-center gap-3">
                <Truck size={18} className="text-slate-600" />
                <div>
                  <h4 className="text-md font-medium text-slate-900">DME & Mobility Equipment</h4>
                </div>
              </div>
              <div>
                {collapsedSections['DME Equipment'] ? <ChevronDown size={20} className="text-slate-600" /> : <ChevronUp size={20} className="text-slate-600" />}
              </div>
            </div>

            <div className={collapsedSections['DME Equipment'] ? 'hidden' : 'p-4 border-t border-slate-200'}>
              <div className="mb-6">
                <h5 className="text-sm font-semibold text-gray-700 mb-3">Mobility Aids</h5>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
                  <CheckboxField field="dmeWheelchair" label="Wheelchair" checked={formData.dmeWheelchair} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="dmeWalker" label="Walker" checked={formData.dmeWalker} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="dmeGaitTrainer" label="Gait Trainer" checked={formData.dmeGaitTrainer} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="dmeCrutches" label="Crutches" checked={formData.dmeCrutches} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="dmeCane" label="Cane" checked={formData.dmeCane} onChange={handleChange}  isReadOnly={isReadOnly} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {formData.dmeWheelchair && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Wheelchair Type</label>
                      <input type="text" value={formData.dmeWheelchairType}
                        onChange={(e) => handleChange('dmeWheelchairType', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        placeholder="e.g., Manual, power, tilt-in-space" />
                    </div>
                  )}
                  {formData.dmeWalker && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Walker Type</label>
                      <input type="text" value={formData.dmeWalkerType}
                        onChange={(e) => handleChange('dmeWalkerType', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        placeholder="e.g., Posterior, anterior, rolling" />
                    </div>
                  )}
                  {formData.dmeGaitTrainer && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Gait Trainer Type</label>
                      <input type="text" value={formData.dmeGaitTrainerType}
                        onChange={(e) => handleChange('dmeGaitTrainerType', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        placeholder="e.g., Rifton Pacer, KidWalk" />
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <h5 className="text-sm font-semibold text-gray-700 mb-3">Positioning Equipment</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <CheckboxField field="dmeStander" label="Stander" checked={formData.dmeStander} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="dmeAdaptiveSeating" label="Adaptive Seating" checked={formData.dmeAdaptiveSeating} onChange={handleChange}  isReadOnly={isReadOnly} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {formData.dmeStander && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Stander Type</label>
                        <input type="text" value={formData.dmeStanderType}
                          onChange={(e) => handleChange('dmeStanderType', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                          placeholder="e.g., EasyStand, Rifton supine" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Stander Schedule</label>
                        <input type="text" value={formData.dmeStanderSchedule}
                          onChange={(e) => handleChange('dmeStanderSchedule', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                          placeholder="e.g., 30 min 2x/day" />
                      </div>
                    </>
                  )}
                  {formData.dmeAdaptiveSeating && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Adaptive Seating Details</label>
                      <textarea value={formData.dmeAdaptiveSeatingDetails}
                        onChange={(e) => handleChange('dmeAdaptiveSeatingDetails', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        rows={2}
                        placeholder="Describe seating system and positioning needs" />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-3">Orthotics & Splints</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <CheckboxField field="dmeAFO" label="AFO" checked={formData.dmeAFO} onChange={handleChange}  isReadOnly={isReadOnly} />
                  <CheckboxField field="dmeKAFO" label="KAFO" checked={formData.dmeKAFO} onChange={handleChange}  isReadOnly={isReadOnly} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {formData.dmeAFO && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">AFO Type</label>
                        <input type="text" value={formData.dmeAFOType}
                          onChange={(e) => handleChange('dmeAFOType', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                          placeholder="e.g., Bilateral solid AFOs, hinged AFOs" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">AFO Wearing Schedule</label>
                        <input type="text" value={formData.dmeAFOWearingSchedule}
                          onChange={(e) => handleChange('dmeAFOWearingSchedule', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                          placeholder="e.g., 4-6 hours daily, check skin q2h" />
                      </div>
                    </>
                  )}
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Equipment</label>
                  <textarea value={formData.dmeOther}
                    onChange={(e) => handleChange('dmeOther', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={2}
                    placeholder="List any additional equipment or supplies" />
                </div>
              </div>
            </div>
          </div>

          {/* ========== PDN CAREGIVER PT TRAINING ========== */}
          {isPediatricPDN && (
            <div className="bg-teal-50 rounded-lg border border-teal-200">
              <div className="flex items-center justify-between cursor-pointer p-4 hover:bg-teal-100 rounded-t-lg transition-colors"
                onClick={() => toggleSection('PDN Caregiver Training')}>
                <div className="flex items-center gap-3">
                  <User size={18} className="text-teal-600" />
                  <div>
                    <h4 className="text-md font-medium text-teal-900">Caregiver PT Training Plan</h4>
                  </div>
                </div>
                <div>
                  {collapsedSections['PDN Caregiver Training'] ? <ChevronDown size={20} className="text-teal-600" /> : <ChevronUp size={20} className="text-teal-600" />}
                </div>
              </div>

              <div className={collapsedSections['PDN Caregiver Training'] ? 'hidden' : 'p-4 border-t border-teal-200'}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Primary Caregiver for PT Training</label>
                    <input type="text" value={formData.pdnPTCaregiverName}
                      onChange={(e) => handleChange('pdnPTCaregiverName', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      placeholder="Name of caregiver to train" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PT Training Status</label>
                    <select value={formData.pdnPTCaregiverTrainingStatus}
                      onChange={(e) => handleChange('pdnPTCaregiverTrainingStatus', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select status</option>
                      <option value="Fully trained">Fully trained - all PT competencies verified</option>
                      <option value="Partially trained">Partially trained - some competencies pending</option>
                      <option value="Training in progress">Training in progress</option>
                      <option value="Not trained">Not trained - training scheduled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Verified PT Competencies</label>
                    <textarea value={formData.pdnPTCaregiverCompetencies}
                      onChange={(e) => handleChange('pdnPTCaregiverCompetencies', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={2}
                      placeholder="e.g., ROM exercises, positioning, transfers, stander use" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PT Training Plan</label>
                    <textarea value={formData.pdnPTCaregiverTrainingPlan}
                      onChange={(e) => handleChange('pdnPTCaregiverTrainingPlan', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={2}
                      placeholder="Training topics and schedule for caregiver PT education" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========== PDN HIP SURVEILLANCE ========== */}
          {isPediatricPDN && (
            <div className="bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center justify-between cursor-pointer p-4 hover:bg-yellow-100 rounded-t-lg transition-colors"
                onClick={() => toggleSection('PDN Hip Surveillance')}>
                <div className="flex items-center gap-3">
                  <AlertTriangle size={18} className="text-yellow-600" />
                  <div>
                    <h4 className="text-md font-medium text-yellow-900">Hip Surveillance (Pediatric CP)</h4>
                  </div>
                </div>
                <div>
                  {collapsedSections['PDN Hip Surveillance'] ? <ChevronDown size={20} className="text-yellow-600" /> : <ChevronUp size={20} className="text-yellow-600" />}
                </div>
              </div>

              <div className={collapsedSections['PDN Hip Surveillance'] ? 'hidden' : 'p-4 border-t border-yellow-200'}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <CheckboxField field="pdnHipSurveillanceRequired" label="Hip Surveillance Required (for CP patients)" checked={formData.pdnHipSurveillanceRequired} onChange={handleChange}  isReadOnly={isReadOnly} />
                  </div>
                  {formData.pdnHipSurveillanceRequired && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Hip X-ray Date</label>
                        <input type="date" value={formData.pdnLastHipXray}
                          onChange={(e) => handleChange('pdnLastHipXray', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hip Status</label>
                        <select value={formData.pdnHipStatus}
                          onChange={(e) => handleChange('pdnHipStatus', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                          <option value="">Select status</option>
                          <option value="Normal - migration <30%">Normal - migration &lt;30%</option>
                          <option value="At risk - migration 30-40%">At risk - migration 30-40%</option>
                          <option value="Subluxation - migration >40%">Subluxation - migration &gt;40%</option>
                          <option value="Dislocation">Dislocation</option>
                          <option value="Post-surgical">Post-surgical</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hip Precautions</label>
                        <textarea value={formData.pdnHipPrecautions}
                          onChange={(e) => handleChange('pdnHipPrecautions', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                          rows={2}
                          placeholder="e.g., Avoid frog-leg positioning, maintain hip abduction in stander" />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ========== DISCHARGE PLANNING ========== */}
          <div className="bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="flex items-center justify-between cursor-pointer p-4 hover:bg-emerald-100 rounded-t-lg transition-colors"
              onClick={() => toggleSection('Discharge Planning')}>
              <div className="flex items-center gap-3">
                <Home size={18} className="text-emerald-600" />
                <div>
                  <h4 className="text-md font-medium text-emerald-900">Discharge Planning (CMS-485 Section 11)</h4>
                </div>
              </div>
              <div>
                {collapsedSections['Discharge Planning'] ? <ChevronDown size={20} className="text-emerald-600" /> : <ChevronUp size={20} className="text-emerald-600" />}
              </div>
            </div>

            <div className={collapsedSections['Discharge Planning'] ? 'hidden' : 'p-4 border-t border-emerald-200'}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discharge Plan</label>
                  <textarea value={formData.dischargePlanning}
                    onChange={(e) => handleChange('dischargePlanning', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={4}
                    placeholder="Anticipated discharge timeline, criteria, recertification plans" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discharge Goals</label>
                  <textarea value={formData.dischargeGoals}
                    onChange={(e) => handleChange('dischargeGoals', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={2}
                    placeholder="Functional criteria for discharge from PT" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Anticipated Discharge Date</label>
                  <input type="date" value={formData.anticipatedDischargeDate}
                    onChange={(e) => handleChange('anticipatedDischargeDate', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Equipment at Discharge</label>
                  <textarea value={formData.equipmentAtDischarge}
                    onChange={(e) => handleChange('equipmentAtDischarge', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={2}
                    placeholder="DME and equipment needs at discharge" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">HEP at Discharge</label>
                  <textarea value={formData.homeExerciseProgramAtDischarge}
                    onChange={(e) => handleChange('homeExerciseProgramAtDischarge', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                    rows={2}
                    placeholder="Exercises for caregiver to continue" />
                </div>
                {isPediatric && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">School/Community Transition</label>
                    <textarea value={formData.pediatricSchoolTransitionPlan}
                      onChange={(e) => handleChange('pediatricSchoolTransitionPlan', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      rows={2}
                      placeholder="Transition to school-based PT or community programs" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ========== PROVIDER INFORMATION ========== */}
          <ProviderSection
            keys={['providerName', 'providerCredentials', 'providerNPI', 'dateCreated']}
            overrides={PT_PROVIDER_OVERRIDES}
            values={formData}
            onChange={handleChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={collapsedSections['Provider Information']}
            onToggle={() => toggleSection('Provider Information')}
          />

          {/* ========== ADDITIONAL NOTES ========== */}
          <div className="bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-100 rounded-t-lg transition-colors"
              onClick={() => toggleSection('Additional Notes')}>
              <div className="flex items-center gap-3">
                <ClipboardList size={18} className="text-gray-600" />
                <div>
                  <h4 className="text-md font-medium text-gray-900">Additional Notes</h4>
                </div>
              </div>
              <div>
                {collapsedSections['Additional Notes'] ? <ChevronDown size={20} className="text-gray-500" /> : <ChevronUp size={20} className="text-gray-500" />}
              </div>
            </div>

            <div className={collapsedSections['Additional Notes'] ? 'hidden' : 'p-4 border-t border-gray-200'}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={formData.additionalNotes}
                  onChange={(e) => handleChange('additionalNotes', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                  rows={3}
                  placeholder="Any additional notes or special considerations..." />
              </div>
            </div>
          </div>

        </div>
      </div>
      </div>
      {draft.dialog}
    </FormPageLayout>
  );
};

export default PTCarePlanForm;
